import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, withHooks, withMethods, withState } from '@ngrx/signals';
import { isNil } from 'lodash';
import { firstValueFrom, from, Observable, of, switchMap, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { EntitlementsDataService } from 'src/shared/services/entitlements.data.service';
import { UserStore } from 'src/shared/stores/user.store';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from 'src/shared/services/auth/auth_service';
import { accountexternal, entities } from 'src/shared/services/client/client';
import { CachedLookupEntry, withEntitlementLookupCache } from './entitlement-lookup-cache.feature';

/** State of an entitlement lookup (e.g. by email). */
export type LookupState = 'idle' | 'loading' | 'complete' | 'error';

/** State of applying membership for a user. */
export type ApplyMembershipState = 'idle' | 'loading' | 'complete' | 'error';

type EntitlementLookupFeatureState = {
  lookupState: LookupState;
  lookupError: string;
  lastLookupResult: CachedLookupEntry | null;
  applyMembershipState: ApplyMembershipState;
  applyMembershipError: string;
  applyMembershipResult: accountexternal.ApplyMembershipResponse | null;
};

const initialState: EntitlementLookupFeatureState = {
  applyMembershipState: 'idle',
  applyMembershipError: '',
  applyMembershipResult: null,
  lookupState: 'idle',
  lookupError: '',
  lastLookupResult: null,
};

/** Builds a cache entry from an API response for a given email. */
function toCachedLookupEntry(email: string, response: entities.EntitlementCalculatorResponse): CachedLookupEntry {
  const now = Date.now();
  const packages = response.active_packages ?? [];
  const kcPackages = packages.filter((p) => p.package_template_code.toLowerCase().startsWith('kc'));
  return {
    email,
    cachedAt: now,
    hasActiveKcMembership: kcPackages.length > 0,
    kcPackages,
  };
}

const emptyResult: CachedLookupEntry = {
  email: '',
  cachedAt: 0,
  hasActiveKcMembership: false,
  kcPackages: [],
};

/**
 * NgRx Signal Store feature for entitlement lookup by email and applying membership.
 * Composes with {@link withEntitlementLookupCache} for caching. Exposes:
 * - lookupByEmail, lookupForCurrentUser – run lookups and update lastLookupResult / lookupState
 * - applyMembershipForUser – apply membership (joii/App only) then re-lookup
 * - clearLookupError, registerMembershipFoundScreenSeen – clear error state and mark screen seen
 */
export function withEntitlementLookup() {
  return signalStoreFeature(
    withState(initialState),
    withEntitlementLookupCache(),
    withMethods((store, entitlementsService = inject(EntitlementsDataService)) => {
      const userStore = inject(UserStore);
      const authenticationService = inject(AuthenticationService);

      /** Looks up entitlements by email, updates cache and lastLookupResult. */
      const lookupByEmail = (email: string): Observable<CachedLookupEntry> =>
        of(email).pipe(
          tap(() => {
            patchState(store, {
              lookupState: 'loading' as LookupState,
            });
          }),
          switchMap(() =>
            entitlementsService.getUserEntitlements().pipe(
              map((response) => toCachedLookupEntry(email, response)),
              switchMap((entry) =>
                from(
                  store.updateCachedFromLookup(email, {
                    hasActiveKcMembership: entry.hasActiveKcMembership,
                    kcPackages: entry.kcPackages,
                  })
                )
              ),
              tapResponse({
                next: (entry) => {
                  patchState(store, {
                    lastLookupResult: entry,
                    lookupState: 'complete' as LookupState,
                  });
                },
                error: (err: unknown) => {
                  patchState(store, {
                    lookupState: 'error' as LookupState,
                    lookupError: err instanceof Error ? err.message : String(err),
                  });
                  return throwError(() => err);
                },
              })
            )
          )
        );

      /** Apply membership for a given user (e.g. after onboarding). Runs only when tenant is joii and app is App. */
      const applyMembershipForUser = async (user: {
        doc_id: string;
        first_name: string;
        last_name: string;
        email: string;
        post_code: string;
      }): Promise<void> => {
        if (authenticationService.tenant()?.friendly_id !== 'joii' || environment.app !== 'App') {
          return;
        }
        const params = {
          provider: ['production', 'sandbox'].includes(environment.environment) ? 'KENNELCLUB' : 'MOCKINTEGRATION',
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          postcode: user.post_code,
        };
        try {
          // Apply membership to user.
          await firstValueFrom(
            of(params).pipe(
              tap(() => {
                patchState(store, {
                  applyMembershipState: 'loading' as ApplyMembershipState,
                });
              }),
              switchMap(() =>
                entitlementsService.applyMembership(user.doc_id, params).pipe(
                  map((result) => {
                    if (isNil(result?.memberships_applied)) {
                      throw new Error('No active plans found for crufts club.');
                    }
                    return result;
                  }),
                  tapResponse({
                    next: (result) => {
                      patchState(store, {
                        applyMembershipState: 'complete' as ApplyMembershipState,
                        applyMembershipResult: result,
                      });
                    },
                    error: (err: unknown) => {
                      patchState(store, {
                        applyMembershipState: 'error' as ApplyMembershipState,
                        applyMembershipError: err instanceof Error ? err.message : String(err),
                      });
                    },
                  })
                )
              ),
              // Lookup user membership and update cache.
              switchMap(() =>
                lookupByEmail(user.email).pipe(
                  catchError((err: any) => {
                    patchState(store, {
                      lookupState: 'error' as LookupState,
                      lookupError: err instanceof Error ? err.message : String(err),
                    });
                    return of(undefined);
                  })
                )
              )
            )
          );
        } catch (err: unknown) {
          throw err;
        }
      };

      return {
        lookupByEmail,
        /** Looks up entitlements for the currently logged-in user. */
        lookupForCurrentUser(): Observable<CachedLookupEntry> {
          const email = userStore.user()?.email;
          if (!email) {
            return of(emptyResult);
          }
          return lookupByEmail(email);
        },
        applyMembershipForUser,
        /** Clears lookup error and resets lookup state to idle. */
        clearLookupError(): void {
          patchState(store, { lookupError: '', lookupState: 'idle' as LookupState });
        },
        clearApplyMembershipError(): void {
          patchState(store, { applyMembershipError: '', applyMembershipState: 'idle' as ApplyMembershipState });
        },
        /** Marks that the membership-found screen was seen for the current user. */
        async registerMembershipFoundScreenSeen(): Promise<void> {
          const email = userStore.user()?.email;
          if (!email) return;
          await store.markMembershipFoundScreenSeen(email);
        },
      };
    }),
    withHooks({
      onInit(store) {
        const userStore = inject(UserStore);
        const email = userStore.user()?.email;
        if (email) {
          store.getCached(email).then((entry) => {
            if (entry) {
              patchState(store, {
                lastLookupResult: entry,
                lookupState: 'complete' as LookupState,
              });
            }
          });
        }
      },
    })
  );
}
